import { useState } from 'react';
import { SimpleSelect, SimpleInput, Alert, Button } from '@/components/ui';
import { PositionRow } from './PositionRow';
import { PositionRowsSkeleton } from './PositionSkeleton';
import type { Position } from '@/types/positionList.types';

interface PositionListProps {
  positions: Position[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onViewDetails: (position: Position) => void;
  onRefresh: () => void;
  onDismissError: () => void;
  refreshing: boolean;
  areas: string[];
  selectedArea: string;
  onAreaChange: (area: string) => void;
  loadingAreas: boolean;
}

export function PositionList({
  positions,
  loading,
  error,
  searchQuery,
  onSearchChange,
  onViewDetails,
  onRefresh,
  onDismissError,
  refreshing,
  areas,
  selectedArea,
  onAreaChange,
  loadingAreas,
}: PositionListProps) {
  const [localSearch, setLocalSearch] = useState(searchQuery);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);
    onSearchChange(value);
  };

  return (
    <div className="position-list-inner min-w-0 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-3">
        <div className="w-full min-w-0 sm:max-w-md">
          <SimpleInput
            type="search"
            placeholder="Search vessels, positions, comments..."
            value={localSearch}
            onChange={handleSearchChange}
            disabled={loading}
            leftIcon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            }
          />
        </div>

        <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
          <SimpleSelect
            value={selectedArea}
            onChange={(e) => onAreaChange(e.target.value)}
            options={areas.map((area) => ({ value: area, label: area }))}
            className="w-full min-w-0 uppercase sm:w-[120px]"
            disabled={loadingAreas || loading}
          />
        </div>
      </div>

      {error && (
        <Alert
          variant="error"
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p className="font-medium">Error loading positions</p>
            <p className="text-sm opacity-90">{error}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onDismissError}>
              Dismiss
            </Button>
            <Button size="sm" onClick={onRefresh} disabled={refreshing}>
              Try Again
            </Button>
          </div>
        </Alert>
      )}

      {!loading && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>
            Showing {positions.length} {positions.length === 1 ? 'vessel' : 'vessels'}
            {searchQuery ? ' matching search' : ''}
          </span>
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setLocalSearch('');
                onSearchChange('');
              }}
              className="shrink-0 font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {loading && <PositionRowsSkeleton />}

      {!loading && positions.length === 0 && !error && (
        <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <svg
                className="h-6 w-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-base font-medium text-gray-900 dark:text-white">
              No positions found
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {searchQuery
                ? 'Try adjusting your search criteria.'
                : 'No position data available for this area.'}
            </p>
          </div>
        </div>
      )}

      {!loading && positions.length > 0 && (
        <div className="position-list-rows min-w-0 space-y-0.5">
          <div
            className="sticky z-10 mb-1 hidden rounded bg-gray-100 p-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:bg-gray-700 dark:text-gray-300 md:grid md:grid-cols-[24px_2fr_80px_120px_1fr_120px_120px] md:items-center md:gap-4 md:px-2"
            style={{ top: '56px' }}
          >
            <div />
            <div>Vessel</div>
            <div className="text-center">Activity</div>
            <div>Last SIRE</div>
            <div>Position</div>
            <div>Date</div>
            <div>Open Date</div>
          </div>

          <div
            className="sticky z-10 mb-1 rounded bg-gray-100 p-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:bg-gray-700 dark:text-gray-300 md:hidden"
            style={{ top: '56px' }}
          >
            <span className="text-gray-700 dark:text-white/70">Tap row to expand</span>
          </div>

          {positions.map((position, index) => (
            <PositionRow
              key={`${position.id}-${position.vesselName}-${index}`}
              position={position}
              onViewDetails={onViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
}
